import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaCheck as FaCheckRaw,
  FaEye as FaEyeRaw,
  FaGraduationCap as FaGraduationCapRaw,
  FaPlus as FaPlusRaw,
  FaRocket as FaRocketRaw,
  FaSearch as FaSearchRaw,
  FaTimes as FaTimesRaw,
  FaUpload as FaUploadRaw,
  FaUserPlus as FaUserPlusRaw,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { supabase } from '../../services/supabase';
import { teacherService, type ClassroomStudentOption } from '../../services/classroom/teacherService';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCheck: any = FaCheckRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEye: any = FaEyeRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaGraduationCap: any = FaGraduationCapRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaRocket: any = FaRocketRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaTimes: any = FaTimesRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUpload: any = FaUploadRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUserPlus: any = FaUserPlusRaw;

const MOBILE_BREAKPOINT = '1024px';

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
  padding-bottom: 0.5rem;
`;

const PageTitle = styled.h1`
  margin: 0 0 1.25rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 2.5vw, 1.65rem);
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 320px);
  gap: 1.25rem;
  align-items: start;
  max-width: 1200px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const SideCol = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.15rem 1.25rem 1.25rem;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1rem;
`;

const IconCircle = styled.span<{ $variant?: 'blue' | 'muted' }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $variant }) =>
    $variant === 'muted' ? 'var(--surfaceVariant)' : 'color-mix(in srgb, var(--primary) 15%, transparent)'};
  color: ${({ $variant }) => ($variant === 'muted' ? 'var(--textSecondary, var(--onSurfaceVariant))' : 'var(--primary)')};
  font-size: 1rem;
`;

const CardTitle = styled.h2`
  margin: 0;
  flex: 1;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
`;

const FieldGroup = styled.div`
  display: grid;
  gap: 1rem;
`;

const FieldLabel = styled.label`
  display: grid;
  gap: 0.45rem;
`;

const LabelText = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const TextInput = styled.input`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
  font-size: 0.9rem;
  background: var(--surface);
  color: var(--text);
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const TextArea = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
  font-size: 0.9rem;
  line-height: 1.45;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  background: var(--surface);
  color: var(--text);
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const ImageUploadArea = styled.label`
  display: grid;
  gap: 0.45rem;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: fit-content;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.55rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--primary);
  background: var(--surfaceVariant);
  cursor: pointer;
`;

const UploadHint = styled.span`
  font-size: 0.76rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const ImagePreview = styled.div`
  width: 100%;
  max-width: 320px;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  overflow: hidden;
`;

const ImagePreviewTag = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StudentCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const StudentHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
`;

const CountBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  background: var(--primaryContainer, var(--surfaceVariant));
  color: var(--primary);
  border: 1px solid var(--outline);
  white-space: nowrap;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  align-items: center;
  margin-bottom: 0.85rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.55rem 0.3rem 0.65rem;
  border-radius: 999px;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text);
`;

const ChipRemove = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--textSecondary);
  cursor: pointer;
  border-radius: 4px;
  &:hover {
    color: var(--error, #b91c1c);
  }
`;

const OthersHint = styled.span`
  font-size: 0.76rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 0.75rem;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--textSecondary);
  font-size: 0.85rem;
  pointer-events: none;
`;

const SearchField = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.7rem 0.85rem 0.7rem 2.35rem;
  font-size: 0.88rem;
  background: var(--surface);
  color: var(--text);
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const StudentListBox = styled.div`
  max-height: 280px;
  overflow: auto;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surfaceVariant);
`;

const StudentRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.65rem;
  align-items: center;
  padding: 0.65rem 0.85rem;
  background: var(--surface);
  &:not(:last-child) {
    border-bottom: 1px solid var(--outline);
  }
`;

const StudentRowMain = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 0.65rem;
  align-items: center;
  min-width: 0;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: var(--primaryContainer, var(--surfaceVariant));
  color: var(--primary);
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid var(--outline);
`;

const StudentName = styled.div`
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--text);
`;

const StudentEmail = styled.div`
  font-size: 0.76rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-top: 0.08rem;
`;

const AddToggleBtn = styled.button<{ $selected: boolean }>`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $selected }) => ($selected ? 'var(--onPrimary)' : 'var(--textSecondary, var(--onSurfaceVariant))')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  &:hover {
    border-color: var(--primary);
    color: ${({ $selected }) => ($selected ? 'var(--onPrimary)' : 'var(--primary)')};
  }
`;

const InviteNote = styled.p`
  margin: 0.85rem 0 0;
  font-size: 0.76rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
`;

const PreviewCard = styled(Card)`
  padding: 1rem 1.1rem;
`;

const PreviewLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--textSecondary);
  margin-bottom: 0.65rem;
`;

const PreviewPlaceholder = styled.div`
  border: 2px dashed var(--outline);
  border-radius: 12px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem;
  font-size: 0.8rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
  background: var(--surfaceVariant);
`;

const PreviewFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.75rem;
  font-size: 0.76rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const FooterBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  max-width: 1200px;
`;

const CreateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: 0;
  border-radius: 12px;
  padding: 0.85rem 2rem;
  min-width: 220px;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.95rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
  &:hover:not(:disabled) {
    background: var(--primaryDark);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
  &:active:not(:disabled) {
    transform: scale(0.99);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const CancelTextLink = styled(Link)`
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  text-decoration: none;
  &:hover {
    color: var(--primary);
  }
`;

const ErrorText = styled.div`
  font-size: 0.84rem;
  color: var(--error, #b91c1c);
  margin-top: -0.25rem;
`;

const EmptyText = styled.div`
  padding: 1rem;
  text-align: center;
  font-size: 0.84rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

function initialsFromName(name: string): string {
  const p = name.split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return `${p[0]!.charAt(0)}${p[1]!.charAt(0)}`.toUpperCase();
}

const MAX_CHIPS = 2;

const ClassroomTeacherNewClassPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);

  const previewName =
    profile?.full_name?.trim() ||
    (typeof user?.user_metadata?.full_name === 'string' && user.user_metadata.full_name.trim()) ||
    user?.email?.split('@')[0] ||
    'Teacher';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [classImageFile, setClassImageFile] = useState<File | null>(null);
  const [classImagePreview, setClassImagePreview] = useState<string | null>(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const studentsQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'students'],
    queryFn: teacherService.getStudentOptions,
  });

  const studentById = useMemo(() => {
    const rows = studentsQuery.data ?? [];
    const m = new Map<string, ClassroomStudentOption>();
    rows.forEach((s) => m.set(s.id, s));
    return m;
  }, [studentsQuery.data]);

  const filteredStudents = useMemo(() => {
    const rows = studentsQuery.data ?? [];
    const q = studentSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((s) => `${s.full_name} ${s.email ?? ''}`.toLowerCase().includes(q));
  }, [studentsQuery.data, studentSearch]);

  const selectedChips = useMemo(() => selectedStudentIds.map((id) => studentById.get(id)).filter(Boolean) as ClassroomStudentOption[], [
    selectedStudentIds,
    studentById,
  ]);

  const extraChipCount = Math.max(0, selectedChips.length - MAX_CHIPS);
  const visibleChips = selectedChips.slice(0, MAX_CHIPS);

  const totalAvailable = (studentsQuery.data ?? []).length;

  const createClass = useMutation({
    mutationFn: teacherService.createClass,
    onSuccess: async (result) => {
      if (!result.ok) {
        setFormError(result.error);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'overview'] });
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'activitySummary'] });
      navigate('/classroom-teacher');
    },
  });

  useEffect(() => {
    return () => {
      if (classImagePreview) URL.revokeObjectURL(classImagePreview);
    };
  }, [classImagePreview]);

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    const cleanName = name.trim();
    if (!cleanName) {
      setFormError('Class name is required.');
      return;
    }
    setFormError(null);
    try {
      let classImageUrl: string | null = null;
      if (classImageFile) {
        if (classImageFile.size > 5 * 1024 * 1024) {
          setFormError('Class image must be 5MB or smaller.');
          return;
        }

        const ext = classImageFile.name.split('.').pop() || 'jpg';
        const fileName = `classroom/class-images/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage.from('post-images').upload(fileName, classImageFile, { upsert: false });
        if (uploadError) {
          setFormError(uploadError.message);
          return;
        }
        const { data: publicData } = supabase.storage.from('post-images').getPublicUrl(fileName);
        classImageUrl = publicData.publicUrl;
      }

      const result = await createClass.mutateAsync({
        name: cleanName,
        description: description.trim() ? description.trim() : null,
        studentIds: selectedStudentIds,
        classImageUrl,
      });

      if (!result.ok) {
        setFormError(result.error);
      }
    } catch (err) {
      setFormError((err as { message?: string }).message ?? 'Unable to create class.');
    }
  };

  const loading = createClass.isPending;
  const studentLoading = studentsQuery.isLoading;

  const toggleStudent = (id: string) => {
    setSelectedStudentIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const removeStudent = (id: string) => {
    setSelectedStudentIds((prev) => prev.filter((v) => v !== id));
  };

  return (
    <Shell>
      <PageTitle>Create New Class</PageTitle>

      <form onSubmit={onSubmit} noValidate>
        <Grid>
          <MainCol>
            <Card>
              <CardHeaderRow>
                <IconCircle>
                  <FaGraduationCap size={18} aria-hidden />
                </IconCircle>
                <CardTitle>Class Details</CardTitle>
              </CardHeaderRow>
              <FieldGroup>
                <FieldLabel>
                  <LabelText>Class name</LabelText>
                  <TextInput
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Advanced Quantum Physics"
                    maxLength={120}
                    autoComplete="off"
                  />
                </FieldLabel>
                <FieldLabel>
                  <LabelText>Class description</LabelText>
                  <TextArea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Briefly describe the learning objectives, course overview, and what students will achieve..."
                    maxLength={1200}
                  />
                </FieldLabel>
                <ImageUploadArea>
                  <LabelText>Class image</LabelText>
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      setClassImageFile(file);
                      if (classImagePreview) URL.revokeObjectURL(classImagePreview);
                      setClassImagePreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                  <UploadButton>
                    <FaUpload size={12} aria-hidden />
                    Upload image
                  </UploadButton>
                  <UploadHint>PNG or JPG, up to 5MB.</UploadHint>
                  {classImagePreview ? (
                    <ImagePreview>
                      <ImagePreviewTag src={classImagePreview} alt="Class preview" />
                    </ImagePreview>
                  ) : null}
                </ImageUploadArea>
              </FieldGroup>
            </Card>

            <Card>
              <StudentCardHeader>
                <StudentHeaderLeft>
                  <IconCircle>
                    <FaUserPlus size={16} aria-hidden />
                  </IconCircle>
                  <CardTitle style={{ margin: 0 }}>Select Students</CardTitle>
                </StudentHeaderLeft>
                <CountBadge>
                  {selectedStudentIds.length} {selectedStudentIds.length === 1 ? 'Student' : 'Students'} Selected
                </CountBadge>
              </StudentCardHeader>

              {selectedChips.length > 0 ? (
                <ChipRow>
                  {visibleChips.map((s) => (
                    <Chip key={s.id}>
                      {s.full_name}
                      <ChipRemove type="button" onClick={() => removeStudent(s.id)} aria-label={`Remove ${s.full_name}`}>
                        <FaTimes size={11} aria-hidden />
                      </ChipRemove>
                    </Chip>
                  ))}
                  {extraChipCount > 0 ? <OthersHint>…and {extraChipCount} other{extraChipCount === 1 ? '' : 's'} selected</OthersHint> : null}
                </ChipRow>
              ) : null}

              <OthersHint style={{ display: 'block', marginBottom: '0.5rem' }}>
                {totalAvailable > 0
                  ? `${totalAvailable} student${totalAvailable === 1 ? '' : 's'} available to enroll.`
                  : 'No students in directory yet.'}
              </OthersHint>

              <SearchWrap>
                <SearchIcon aria-hidden>
                  <FaSearch size={14} />
                </SearchIcon>
                <SearchField
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  placeholder="Search students by name or email..."
                  aria-label="Search students"
                />
              </SearchWrap>

              <StudentListBox>
                {studentLoading ? (
                  <EmptyText>Loading students…</EmptyText>
                ) : filteredStudents.length === 0 ? (
                  <EmptyText>No students match your search.</EmptyText>
                ) : (
                  filteredStudents.map((student) => {
                    const selected = selectedStudentIds.includes(student.id);
                    return (
                      <StudentRow key={student.id}>
                        <StudentRowMain>
                          <Avatar aria-hidden>{initialsFromName(student.full_name)}</Avatar>
                          <div style={{ minWidth: 0 }}>
                            <StudentName>{student.full_name}</StudentName>
                            <StudentEmail>{student.email ?? '—'}</StudentEmail>
                          </div>
                        </StudentRowMain>
                        <AddToggleBtn
                          type="button"
                          $selected={selected}
                          onClick={() => toggleStudent(student.id)}
                          aria-label={selected ? `Remove ${student.full_name} from class` : `Add ${student.full_name} to class`}
                        >
                          {selected ? <FaCheck size={14} aria-hidden /> : <FaPlus size={14} aria-hidden />}
                        </AddToggleBtn>
                      </StudentRow>
                    );
                  })
                )}
              </StudentListBox>

              <InviteNote>Student invites will be sent automatically once the class is created.</InviteNote>
            </Card>
          </MainCol>

          <SideCol>
            <PreviewCard>
              <PreviewLabel>Class preview</PreviewLabel>
              <PreviewPlaceholder>
                {name.trim()
                  ? `“${name.trim()}” — your class card will show on the dashboard once created.`
                  : 'Your class card will appear here once you fill in the details.'}
              </PreviewPlaceholder>
              <PreviewFooter>
                <FaEye size={13} aria-hidden />
                <span>
                  Previewing as: <strong>{previewName}</strong>
                </span>
              </PreviewFooter>
            </PreviewCard>
          </SideCol>
        </Grid>

        {formError ? <ErrorText style={{ marginTop: '1rem' }}>{formError}</ErrorText> : null}

        <FooterBar>
          <CreateBtn type="submit" disabled={loading}>
            <FaRocket size={15} aria-hidden />
            {loading ? 'Creating…' : 'Create Class'}
          </CreateBtn>
          <CancelTextLink to="/classroom-teacher">Cancel</CancelTextLink>
        </FooterBar>
      </form>
    </Shell>
  );
};

export default ClassroomTeacherNewClassPage;
